import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import type { Column } from "@tanstack/react-table";
import { type MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { fieldKindFor } from "@storyboard/dispatch-view/fieldKinds";
import type { RequiresAcknowledgementRef } from "@storyboard/dispatch-view/requiresAcknowledgement";
import { resolveRequiresAcknowledgement } from "@storyboard/dispatch-view/requiresAcknowledgement";
import type { ResolvedDispatchView } from "@storyboard/dispatch-view/resolve";
import type { DispatchGrain, DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import { isIntegrationTag } from "@storyboard/driver-query/tagColumns";
import { updateMembershipConfig } from "@storyboard/web-client";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { ampli, transport } from "@/config";
import { acknowledgeGrainFor } from "@/hooks/dispatch/dispatchModel";
import { type DispatchTableRow, rowFeedId } from "@/hooks/dispatch/dispatchTableRow";
import type { AcknowledgementTrigger, ContextOpenTrigger, ReplyTrigger } from "@/hooks/dispatch/dispatchTracking";
import { fleetRecipientsFor } from "@/hooks/dispatch/fleetRecipients";
import { VIEW_QUERY_PARAM } from "@/hooks/dispatch/resolveActiveView";
import { useAcknowledgements } from "@/hooks/dispatch/useAcknowledgements";
import { useDispatchComposer } from "@/hooks/dispatch/useDispatchComposer";
import { useDispatchFieldOptions } from "@/hooks/dispatch/useDispatchFieldOptions";
import { useDispatchHighlight } from "@/hooks/dispatch/useDispatchHighlight";
import { useDispatchItemRows } from "@/hooks/dispatch/useDispatchItemRows";
import { useDispatchPanelRoute } from "@/hooks/dispatch/useDispatchPanelRoute";
import { useDispatchQuery } from "@/hooks/dispatch/useDispatchQuery";
import { useDispatchRows } from "@/hooks/dispatch/useDispatchRows";
import { type DispatchShortcutActions, useDispatchShortcuts } from "@/hooks/dispatch/useDispatchShortcuts";
import { useDispatchTableLayout } from "@/hooks/dispatch/useDispatchTableLayout";
import { useDispatchTitleFlash } from "@/hooks/dispatch/useDispatchTitleFlash";
import { useFeedsUnacknowledgedCounts } from "@/hooks/dispatch/useFeedsUnacknowledged";
import { useMinuteClock } from "@/hooks/dispatch/useMinuteClock";
import { useReadHighlighted } from "@/hooks/dispatch/useReadHighlighted";
import { useResolvedDispatchView } from "@/hooks/dispatch/useResolvedDispatchView";
import { useTickedRows } from "@/hooks/dispatch/useTickedRows";
import { useUnacknowledgedBadge } from "@/hooks/dispatch/useUnacknowledgedBadge";
import { useDriverTags } from "@/hooks/driver/useDriverTags";
import { useLatestCallback } from "@/hooks/useLatestCallback";
import { useTeamAliases } from "@/hooks/useTeamAliases";
import { UserContext } from "@/models/StateProviders/userProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { UxContext } from "@/models/UxStateProvider";
import type { TagDefinition } from "@/types/driver";
import type { DataTableFeatures } from "@/ui/dataTableFeatures";
import { brandPrimary, zinc } from "@/ui/tokens";
import { DRIVER_BADGE_CAP, isStructuralColumnId } from "./columns";
import DispatchBulkActions from "./DispatchBulkActions";
import DispatchColumnMenu, { type ColumnMenuTarget } from "./DispatchColumnMenu";
import { DISPATCH_PANEL_COMPOSER_INPUT_ID } from "./DispatchComposer";
import DispatchDisplayMenu, { type DisplaySort } from "./DispatchDisplayMenu";
import DispatchFilterMenu, { DispatchSearchBox } from "./DispatchFilterMenu";
import DispatchFleetMessage from "./DispatchFleetMessage";
import DispatchPopover, { type DispatchPanel } from "./DispatchPopover";
import DispatchQueryStrip from "./DispatchQueryStrip";
import DispatchRowDetail from "./DispatchRowDetail";
import DispatchRowMenu from "./DispatchRowMenu";
import DispatchSelectionBar from "./DispatchSelectionBar";
import { DispatchShortcutsBar, DispatchShortcutsButton } from "./DispatchShortcutsBar";
import DispatchShortcutsList from "./DispatchShortcutsList";
import DispatchTable, { dispatchPageRowCount } from "./DispatchTable";
import DispatchTeamPicker from "./DispatchTeamPicker";
import DispatchViewPicker from "./DispatchViewPicker";
import { FILTERED_ACTION_SX } from "./dispatchColors";
import type { DispatchPanelHandle } from "./panelScroll";
import { DispatchSearchHighlight } from "./searchHighlight";

/**
 * "N of M drivers" at driver grain, filtered or not, as the classic driver list counts; at message grain "N messages",
 * or "N of M" while the filters hide some of them.
 */
function countLabel(visible: number, total: number, noun: DispatchGrain): string {
  const plural = total === 1 ? noun : `${noun}s`;
  if (noun === "message" && visible === total) return `${visible} ${plural}`;
  return `${visible} of ${total} ${plural}`;
}

/** "N matches in all M drivers": a search runs over every row, so the count says so rather than "of". */
function matchLabel(visible: number, total: number, noun: DispatchGrain): string {
  return `${visible} ${visible === 1 ? "match" : "matches"} in all ${total} ${total === 1 ? noun : `${noun}s`}`;
}

/**
 * The header's count: matches while a search runs, else the rows shown out of all of them.
 *
 * The count holds the width of the longest label its total can produce, so the search box beside it never moves as a
 * search starts, changes, or clears. An invisible copy of that label shares the count's grid cell and sets the width;
 * tabular digits keep every number at the width the copy measured.
 */
function DispatchCount({
  visible,
  total,
  noun,
  filterCount,
  searching,
}: {
  visible: number;
  total: number;
  noun: DispatchGrain;
  filterCount: number;
  searching: boolean;
}) {
  // While a search or filters narrow the rows the whole count takes the brand color, so it reads as narrowed at a
  // glance; the icon says which of the two is in force.
  const narrowed = searching || filterCount > 0;
  const cellSx = { gridArea: "1 / 1", display: "inline-flex", alignItems: "center", gap: 0.5 } as const;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-grid",
        ml: 1,
        color: narrowed ? brandPrimary[300] : zinc[500],
        fontSize: "0.8125rem",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      <Box component="span" aria-hidden sx={{ ...cellSx, visibility: "hidden" }}>
        <SearchIcon sx={{ fontSize: 14 }} />
        {matchLabel(total, total, noun)}
      </Box>
      <Typography
        component="span"
        data-sb={TestIds.dispatch.count}
        sx={{ ...cellSx, font: "inherit", color: "inherit" }}
      >
        {searching && <SearchIcon aria-label="Searching" sx={{ fontSize: 14 }} />}
        {!searching && filterCount > 0 && <FilterAltIcon aria-label="Filtered" sx={{ fontSize: 14 }} />}
        {searching ? matchLabel(visible, total, noun) : countLabel(visible, total, noun)}
      </Typography>
    </Box>
  );
}

/** The table's empty state while filters hide every row: says so, and offers the way back. */
function DispatchFilteredEmpty({
  noun,
  filterCount,
  onClear,
}: {
  noun: DispatchGrain;
  filterCount: number;
  onClear: () => void;
}) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      No {noun}s match {filterCount} {filterCount === 1 ? "filter" : "filters"}
      <ButtonBase
        onClick={onClear}
        sx={{
          height: 26,
          px: 1.25,
          borderRadius: 1,
          fontSize: "0.75rem",
          fontWeight: 600,
          ...FILTERED_ACTION_SX,
        }}
      >
        Clear filters
      </ButtonBase>
    </Box>
  );
}

/** What the table says with no rows: nothing matches the search, the filters hide every row, or there are none. */
function DispatchNoRows({
  noun,
  search,
  filterCount,
  total,
  onClearFilters,
}: {
  noun: DispatchGrain;
  /** The search term in force; blank when none runs. */
  search: string;
  filterCount: number;
  total: number;
  onClearFilters: () => void;
}) {
  if (search !== "") return <>{`No ${noun}s match "${search}"`}</>;
  if (filterCount > 0 && total > 0) {
    return <DispatchFilteredEmpty noun={noun} filterCount={filterCount} onClear={onClearFilters} />;
  }
  return <>{`No ${noun}s`}</>;
}

/** Where the row menu opened: the row, the column of the cell right-clicked (none off every cell), the pointer. */
type RowMenuAnchor = { row: DispatchTableRow; columnId: string | null; position: { top: number; left: number } };

/** What `useResolvedDispatchView` yields once the cascade offers a view: the definition and the resolved view, present. */
type ActiveView = Omit<ReturnType<typeof useResolvedDispatchView>, "definition" | "resolved"> & {
  definition: DispatchViewDefinition;
  resolved: ResolvedDispatchView;
};

/**
 * The Dispatch page: one row per driver, or per message when the view says so, laid out by the active view
 * definition with the member's state over it.
 *
 * Resolves the view (from `?view=`, the member's state, and the page settings) and hands it to {@link DispatchDesk};
 * without a view in the cascade there is nothing to lay out, and the page says so.
 */
export default function DispatchView() {
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { tags: workspaceTags } = useDriverTags();
  // Which integration a driver came from is a backend concern; the page never shows that tag anywhere.
  const tags = useMemo(() => workspaceTags.filter((tag) => !isIntegrationTag(tag.slug)), [workspaceTags]);
  const view = useResolvedDispatchView(tags);
  if (!view.definition || !view.resolved) {
    return (
      <Box sx={{ p: 3, color: zinc[400] }}>
        <Typography>No Dispatch view is configured for this workspace.</Typography>
      </Box>
    );
  }
  return (
    <DispatchDesk
      workspaceId={workspaceId ?? ""}
      membershipId={myCurrentWorkspaceMembership?.id}
      tags={tags}
      view={{ ...view, definition: view.definition, resolved: view.resolved }}
    />
  );
}

/**
 * The page under a resolved view.
 *
 * The page has two modes. With no row ticked it is one driver at a time: the highlight unfolds a row into its detail
 * and reply, and every key acts on that row. With any row ticked it is a spreadsheet selection: the detail folds away
 * and A, Shift+M, and the header's actions act on the ticked rows. The rules the keys follow (what an acknowledgement
 * acts on, how it differs from a read, that opening History never acknowledges, where the highlight lands when a row
 * vanishes, who a fleet message goes to) are stated once in `hooks/dispatch/dispatchModel.ts`; the hooks under
 * `hooks/dispatch/` each own one part (the popover's URL, the query, the table, the acknowledgements, the ticked
 * rows, the composer), and this component wires them together.
 */
function DispatchDesk({
  workspaceId,
  membershipId,
  tags,
  view,
}: {
  workspaceId: string;
  membershipId: string | undefined;
  tags: readonly TagDefinition[];
  view: ActiveView;
}) {
  const { definition, resolved, state, views, selectView, write, reset, page } = view;
  const { keymap } = page;
  const { setGlobalSearchOpen } = useContext(UxContext);

  // The team (channel) is the member's `dispatcherFunction`, the same choice the driver list keeps, so the two pages
  // read and open the same channel per driver.
  const { team, availableTeams, teamAliases } = useTeamAliases(workspaceId, membershipId ?? "");
  const { setDispatcherFunction } = useContext(UserContext);
  const onTeamChange = useCallback(
    (next: string) => {
      if (next !== team) ampli.dispatchTeamSwitched({ workspaceId, fromTeam: team, toTeam: next });
      setDispatcherFunction?.(next);
      if (!workspaceId || !membershipId) return;
      updateMembershipConfig(transport, { workspaceId, membershipId, config: { dispatcherFunction: next } }).catch(
        () => {
          // The optimistic choice already applied; the next successful write carries it.
        },
      );
    },
    [workspaceId, membershipId, setDispatcherFunction, team],
  );

  // The view picker's two events: the dropdown opening, and a choice of another view.
  const onViewPickerOpen = useCallback(
    () => ampli.dispatchViewOpened({ workspaceId, viewName: definition.label }),
    [workspaceId, definition.label],
  );
  const switchView = useCallback(
    (viewId: string) => {
      const next = views.find((candidate) => candidate.id === viewId);
      if (next && next.id !== definition.id) {
        ampli.dispatchViewSwitched({ workspaceId, fromView: definition.label, toView: next.label });
      }
      selectView(viewId);
    },
    [workspaceId, views, definition.id, definition.label, selectView],
  );

  const requiresAcknowledgementControl = useMemo(
    () => ({
      include: resolved.requiresAcknowledgement.include,
      overridden: resolved.overrides.requiresAcknowledgement,
      onChange: (include: RequiresAcknowledgementRef[]) => write({ requiresAcknowledgement: { include } }),
      // `null` takes the person's list back, so the view's (or the workspace's default) applies again.
      onReset: () => write({ requiresAcknowledgement: null }),
    }),
    [resolved.requiresAcknowledgement, resolved.overrides.requiresAcknowledgement, write],
  );
  // What this person is asked to acknowledge: their own list, else the view's, else driver messages alone. The rows, the
  // dot, and the message-grain items all read it, so they never disagree on what counts.
  const requires = useMemo(
    () => resolveRequiresAcknowledgement(resolved.requiresAcknowledgement),
    [resolved.requiresAcknowledgement],
  );
  const { rows } = useDispatchRows(workspaceId, membershipId, teamAliases, requires);
  const items = useDispatchItemRows(workspaceId, membershipId, resolved.grain, teamAliases, requires);
  const now = useMinuteClock();
  // How many things wait on a dispatcher per channel, folded from the acknowledgement log; the read state rides on
  // the rows.
  const channelFeedIds = useMemo(() => rows.flatMap((driver) => (driver.feedId ? [driver.feedId] : [])), [rows]);
  const unacknowledgedByFeed = useFeedsUnacknowledgedCounts(channelFeedIds, now, requires);
  const tagsBySlug = useMemo(() => new Map<string, TagDefinition>(tags.map((tag) => [tag.slug, tag])), [tags]);
  const tagIdBySlug = useMemo(() => new Map<string, string>(tags.map((tag) => [tag.slug, tag.id])), [tags]);

  const pageRef = useRef<HTMLDivElement>(null);
  // Hands the keyboard back to the table so the next key is a shortcut again; `r` re-enters the composer.
  const focusPage = useCallback(() => pageRef.current?.focus(), []);
  // The header's search box, which the `focusSearch` key puts the cursor in; Escape hands the keys back from there.
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const route = useDispatchPanelRoute(workspaceId, searchParams.get(VIEW_QUERY_PARAM), focusPage);
  const { openFeedId, openMembershipId, panel } = route;
  const openDriver = useMemo(
    () => rows.find((row) => row.workspaceMembershipId === openMembershipId) ?? null,
    [rows, openMembershipId],
  );

  const query = useDispatchQuery({
    workspaceId,
    definition,
    resolved,
    rows,
    items,
    tagsBySlug,
    tagIdBySlug,
    openMembershipId,
    now,
    unacknowledgedByFeed,
    write,
  });
  const { tableRows, filtered, filteredByView, total, chips, filterCount, searching } = query;
  // A hidden tab flashes its title when a driver in the current view gains unread traffic. A search is a quick look
  // up, so both watch the view's own rows rather than its matches.
  useDispatchTitleFlash(filteredByView);
  // The favicon and title count the rows the filters leave showing a red dot, not the unread channels.
  useUnacknowledgedBadge(filteredByView);

  const { highlighted, highlightedId, highlight, move, advance } = useDispatchHighlight(tableRows);
  // Selecting a driver while their neighbour's History or Context is open moves the open tab to them, whether the
  // selection came from the keys, a click, or handling the row before it. Only a selection moves it: a row vanishing
  // under the highlight does not.
  const select = useCallback(
    (row: DispatchTableRow | null) => {
      if (row) route.followRow(row);
    },
    [route.followRow],
  );
  // Handling a row, by A or by a reply from the row, moves the selection to the row after it in the list as it stood
  // when the key or Send was pressed, so a row the handling regroups or filters out cannot change where it lands.
  const advanceAndFollow = useCallback(() => select(advance()), [select, advance]);
  // The row under the pointer, so a Shift run with nothing highlighted can start where the dispatcher is looking. A
  // ref, not state: hovering must not re-render the table.
  const hoveredRowIdRef = useRef<string | null>(null);
  const openRowId = useMemo(
    () => (openFeedId ? (tableRows.find((row) => rowFeedId(row) === openFeedId)?.id ?? null) : null),
    [tableRows, openFeedId],
  );
  const ticked = useTickedRows({
    tableRows,
    viewId: resolved.id,
    highlightedId,
    highlight,
    openRowId,
    hoveredRowIdRef,
  });
  const { tickedRows, tickedMode } = ticked;

  const acknowledgements = useAcknowledgements(acknowledgeGrainFor(resolved.grain), {
    workspaceId,
    membershipId,
    requires,
  });
  const { acknowledgeRow, unacknowledgeRow, acknowledgeFeed, undo } = acknowledgements;
  // Every reply the page sends is reported with where it was written and how Send was pressed; the channel's team is
  // the one the page reads, since every row's channel is that team's.
  const trackReply = useCallback(
    (driverMembershipId: string, feedId: string, source: "row" | "history" | "context", trigger: ReplyTrigger) =>
      ampli.dispatchReplySent({
        workspaceId,
        workspaceMembershipId: driverMembershipId,
        feedId,
        channelAlias: team || undefined,
        source,
        trigger,
      }),
    [workspaceId, team],
  );
  // A reply handles the row it answers: it acknowledges the row at the view's grain, as A does, and hands the selection
  // on. The keyboard comes back to the page.
  const afterReply = useCallback(
    (row: DispatchTableRow, feedId: string, trigger: ReplyTrigger) => {
      trackReply(row.driver.workspaceMembershipId, feedId, "row", trigger);
      acknowledgeRow(row, feedId, "reply");
      focusPage();
      advanceAndFollow();
    },
    [trackReply, acknowledgeRow, focusPage, advanceAndFollow],
  );
  const composer = useDispatchComposer({ highlighted, workspaceId, onSent: afterReply });
  // A reply from History or Context answers the open driver's whole conversation, so it acknowledges their channel
  // whatever the view's grain. The dispatcher is still mid-conversation, so the selection and the cursor stay put.
  const afterPanelReply = useCallback(
    (trigger: ReplyTrigger) => {
      if (!openFeedId) return;
      if (openMembershipId) trackReply(openMembershipId, openFeedId, panel, trigger);
      acknowledgeFeed(openFeedId, "reply");
    },
    [openFeedId, openMembershipId, panel, trackReply, acknowledgeFeed],
  );

  const badgeCap = DRIVER_BADGE_CAP;
  const layout = useDispatchTableLayout({
    definition,
    resolved,
    tagsBySlug,
    now,
    tableRows,
    ticks: ticked.ticks,
    onTicksChange: ticked.setTicks,
    filteredColumnIds: query.filteredColumnIds,
    shiftSelectRef: ticked.shiftSelectRef,
    write,
    badgeCap,
  });
  const { table } = layout;
  const options = useDispatchFieldOptions({ definition, resolved, tags, tagsBySlug, naming: query.naming });

  // --- The keys: on the highlighted row alone, or on the ticked rows once any are ticked ---
  const onHighlighted = useCallback(
    (act: (row: DispatchTableRow, feedId: string) => void, andAdvance: boolean) => {
      if (!highlighted) return;
      const feedId = rowFeedId(highlighted);
      if (feedId) act(highlighted, feedId);
      if (andAdvance) advanceAndFollow();
    },
    [highlighted, advanceAndFollow],
  );
  const onTicked = useCallback(
    (act: (rows: readonly DispatchTableRow[]) => void) => {
      act(tickedRows);
      ticked.clear();
    },
    [tickedRows, ticked.clear],
  );
  // Each acknowledgement names what triggered it: the key, the row's buttons, or the header's button over the ticks.
  const acknowledgeTicked = useCallback(
    (trigger: AcknowledgementTrigger) => onTicked((rows) => acknowledgements.acknowledgeRows(rows, trigger)),
    [onTicked, acknowledgements.acknowledgeRows],
  );
  const acknowledgeTickedByButton = useCallback(() => acknowledgeTicked("bulkAction"), [acknowledgeTicked]);
  const acknowledge = useCallback(
    (trigger: AcknowledgementTrigger) => onHighlighted((row, feedId) => acknowledgeRow(row, feedId, trigger), true),
    [onHighlighted, acknowledgeRow],
  );
  const acknowledgeChannel = useCallback(
    (trigger: AcknowledgementTrigger) => onHighlighted((_row, feedId) => acknowledgeFeed(feedId, trigger), true),
    [onHighlighted, acknowledgeFeed],
  );
  // Opening Context is reported with what opened it, and only when it opened: the key on an open Context closes it.
  const trackContextOpen = useCallback(
    (driverMembershipId: string, accountId: string | null, trigger: ContextOpenTrigger) =>
      ampli.driverContextOpen({
        workspaceId,
        workspaceMembershipId: driverMembershipId,
        accountId: accountId ?? undefined,
        trigger,
      }),
    [workspaceId],
  );
  const openContext = useCallback(
    (row: DispatchTableRow, trigger: ContextOpenTrigger) => {
      if (route.openPanel("context", row))
        trackContextOpen(row.driver.workspaceMembershipId, row.driver.accountId, trigger);
    },
    [route.openPanel, trackContextOpen],
  );
  // The popover's tabs: a switch onto Context is an opening too.
  const changePanel = useCallback(
    (next: DispatchPanel) => {
      route.changePanel(next);
      if (next === "context" && openMembershipId)
        trackContextOpen(openMembershipId, openDriver?.accountId ?? null, "tab");
    },
    [route.changePanel, openMembershipId, openDriver?.accountId, trackContextOpen],
  );
  const focusComposer = useCallback(() => {
    if (!highlighted) move(1);
    composer.focus();
  }, [highlighted, move, composer.focus]);
  // The popover's composer, on either tab; next frame, as `useComposer.focus` does. A disabled box takes no focus.
  const focusPanelComposer = useCallback(() => {
    requestAnimationFrame(() => document.getElementById(DISPATCH_PANEL_COMPOSER_INPUT_ID)?.focus());
  }, []);

  // --- Fleet message: to the ticked drivers, else to every driver the page shows ---
  const [fleetOpen, setFleetOpen] = useState(false);
  const fleetRecipients = useMemo(
    () => fleetRecipientsFor(tickedMode ? tickedRows : filtered),
    [tickedMode, tickedRows, filtered],
  );
  const openFleetMessage = useCallback(() => {
    if (fleetRecipients.length > 0) setFleetOpen(true);
  }, [fleetRecipients.length]);
  // The composer repeats the narrowing behind "every driver shown" as chips: the search while it runs, since it sets the
  // filters aside, else the filters; the sort is not a narrowing.
  const fleetFilters = useMemo(
    () =>
      searching
        ? [`Search: ${query.search.trim()}`]
        : chips.filter((chip) => chip.source !== "display").map((chip) => chip.summary),
    [searching, query.search, chips],
  );
  // The table's cells mark what the search matched in the fields it scans.
  const searchHighlight = useMemo(
    () => ({ term: searching ? query.search.trim() : "", fields: new Set<string>(resolved.query.search) }),
    [searching, query.search, resolved.query.search],
  );

  // --- The shortcuts: the keyboard button folds the bar, a persisted page setting; the key opens the full list ---
  // The keyboard button the list anchors under, held in state so the anchor is never read off a ref during render.
  const [shortcutsButton, setShortcutsButton] = useState<HTMLButtonElement | null>(null);
  const [shortcutsListOpen, setShortcutsListOpen] = useState(false);
  const openShortcutsList = useCallback(() => setShortcutsListOpen(true), []);
  const closeShortcutsList = useCallback(() => setShortcutsListOpen(false), []);
  const { showShortcuts: shortcutBarShown, setShowShortcuts: setShortcutBarShown } = page;
  const toggleShortcutBar = useCallback(() => {
    if (shortcutBarShown) ampli.hideKeyboardShortcut({ workspaceId });
    else ampli.showKeyboardShortcut({ workspaceId });
    setShortcutBarShown(!shortcutBarShown);
  }, [setShortcutBarShown, shortcutBarShown, workspaceId]);

  // --- The page keys: a screenful of rows, or a page of the popover's tab (History or Context) while it is open ---
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<DispatchPanelHandle>(null);
  const panelShown = openFeedId != null;

  // Measured on the key press, so a resized window pages by what it now shows. In ticked mode it moves the highlight
  // and ticks nothing, as the row keys do.
  const turnPage = (direction: 1 | -1) => {
    if (panelShown) {
      panelRef.current?.scrollPage(direction);
      return;
    }
    select(move(direction * dispatchPageRowCount(tableAreaRef.current?.clientHeight ?? 0)));
  };

  const quiet = () => {};
  const escapeAction = () => {
    if (openFeedId != null) return route.closePanel;
    if (tickedMode) return ticked.clear;
    if (searching) return query.clearSearch;
    return route.closePanel;
  };
  const shortcutActions: DispatchShortcutActions = {
    nextRow: () => select(move(1)),
    prevRow: () => select(move(-1)),
    nextPage: () => turnPage(1),
    prevPage: () => turnPage(-1),
    // With History or Context open the reply goes from there, ticked rows or not; otherwise from the row.
    reply: openFeedId != null ? focusPanelComposer : tickedMode ? quiet : focusComposer,
    send: () => void composer.send("keyboard"),
    acknowledge: tickedMode ? () => acknowledgeTicked("keyboard") : () => acknowledge("keyboard"),
    acknowledgeChannel: tickedMode
      ? () => onTicked((rows) => acknowledgements.acknowledgeChannelsOf(rows, "keyboard"))
      : () => acknowledgeChannel("keyboard"),
    // Unacknowledging stays put: the row is now waiting again, and the dispatcher is looking at it.
    unacknowledge: tickedMode
      ? () => onTicked((rows) => acknowledgements.unacknowledgeRows(rows, "keyboard"))
      : () => onHighlighted((row, feedId) => unacknowledgeRow(row, feedId, "keyboard"), false),
    toggleSelect: ticked.toggle,
    openHistory: tickedMode ? quiet : () => onHighlighted((row) => route.openPanel("history", row), false),
    openContext: tickedMode ? quiet : () => onHighlighted((row) => openContext(row, "keyboard"), false),
    fleetMessage: openFleetMessage,
    // SearchContainer has no scope prop yet, so the workspace search opens unscoped.
    searchDriver: () => setGlobalSearchOpen?.(true),
    // The key's own keydown is prevented, so it never types into the box it focuses; no selection changes.
    focusSearch: () => searchInputRef.current?.focus(),
    // Escape closes what is open first, then clears the ticked rows, then the search, which brings the filters back.
    closePanel: escapeAction(),
    // The list stands the page's keys down while open and closes on this key itself.
    showShortcuts: () => setShortcutsListOpen((open) => !open),
    extendSelectionDown: () => ticked.extend(1),
    extendSelectionUp: () => ticked.extend(-1),
    selectAll: ticked.all,
    undo,
  };
  useDispatchShortcuts(keymap, shortcutActions, !fleetOpen && !shortcutsListOpen, panelShown);

  // A deep link (or reload) onto a driver's popover puts the highlight on that driver too. The row id is the
  // membership id unless grouping copied the driver, so the lookup goes through the current rows without re-running
  // (and re-highlighting) on every data refresh. When the popover followed the selection the highlight is already on
  // one of that driver's rows, and stays on it.
  const tableRowsRef = useRef(tableRows);
  tableRowsRef.current = tableRows;
  const highlightedRef = useRef(highlighted);
  highlightedRef.current = highlighted;
  useEffect(() => {
    if (!openMembershipId || highlightedRef.current?.driver.workspaceMembershipId === openMembershipId) return;
    const row = tableRowsRef.current.find((candidate) => candidate.driver.workspaceMembershipId === openMembershipId);
    highlight(row?.id ?? openMembershipId);
  }, [openMembershipId, highlight]);

  // A right-click opens the row menu, which offers Only / Exclude on the clicked cell's value. A column's id is its
  // field key, which is what the menu matches on. Both row handlers keep one identity: `select` changes whenever the
  // open driver or panel does, and a new handler would re-render every row on each J/K with History open.
  const [rowMenu, setRowMenu] = useState<RowMenuAnchor | null>(null);
  const onRowClick = useLatestCallback((row: DispatchTableRow) => {
    highlight(row.id);
    select(row);
  });
  const openRowMenu = useLatestCallback((row: DispatchTableRow, event: MouseEvent) => {
    highlight(row.id);
    select(row);
    const columnId = (event.target as HTMLElement).closest<HTMLElement>("[data-col-id]")?.dataset.colId ?? null;
    setRowMenu({ row, columnId, position: { top: event.clientY, left: event.clientX } });
  });
  // A click on a header opens that column's menu, anchored to the cell: sort, a filter editor for its kind, pin, hide.
  const [columnMenu, setColumnMenu] = useState<ColumnMenuTarget | null>(null);
  const onHeaderClick = useCallback(
    (column: Column<DataTableFeatures, DispatchTableRow>, event: MouseEvent<HTMLElement>) => {
      if (isStructuralColumnId(column.id)) return;
      setColumnMenu({
        columnId: column.id,
        label: layout.labelFor(column.id),
        kind: fieldKindFor(column.id, tagsBySlug),
        anchorEl: event.currentTarget,
      });
    },
    [layout.labelFor, tagsBySlug],
  );
  const { sort } = resolved.query;
  const displaySort: DisplaySort = sort ? { key: sort.field, direction: sort.direction } : null;
  // The grain the view itself lays out; choosing it hands the grain back rather than pinning it (`DispatchDisplayMenu`).
  const viewGrain: DispatchGrain = definition.grain ?? "driver";
  const changeGrain = useCallback(
    (grain: DispatchGrain | null) => {
      const next = grain ?? viewGrain;
      if (next !== resolved.grain)
        ampli.driverFeedViewChange({ workspaceId, fromFeedView: resolved.grain, toFeedView: next });
      write({ grain });
    },
    [workspaceId, viewGrain, resolved.grain, write],
  );

  // The highlighted row unfolds into its detail: the inline reply and the row actions.
  // Highlighting a row reads the message its cells show; ticked rows, however many, read nothing.
  useReadHighlighted(tickedMode ? null : highlighted);
  const renderExpandedRow = useCallback(
    (row: DispatchTableRow) => (
      <DispatchRowDetail
        row={row}
        reply={composer}
        keymap={keymap}
        onOpenHistory={() => route.openPanel("history", row)}
        onOpenContext={() => openContext(row, "rowAction")}
        onAcknowledge={() => acknowledge("rowAction")}
        onAcknowledgeChannel={() => acknowledgeChannel("rowAction")}
      />
    ),
    [composer, keymap, route.openPanel, openContext, acknowledge, acknowledgeChannel],
  );

  // The row under the pointer anchors a Shift-arrow run when nothing is highlighted.
  const onTableMouseOver = useCallback((event: MouseEvent<HTMLElement>) => {
    hoveredRowIdRef.current =
      (event.target as HTMLElement).closest<HTMLElement>("tr[data-row-id]")?.dataset.rowId ?? null;
  }, []);
  const clearHoveredRow = useCallback(() => {
    hoveredRowIdRef.current = null;
  }, []);
  const getRowClassName = useCallback(
    (row: DispatchTableRow) => {
      const classes: string[] = [];
      if (ticked.ticks[row.id]) classes.push("dispatch-row-checked");
      if (row.id === highlightedId) classes.push("dispatch-row-selected");
      if (openFeedId != null && rowFeedId(row) === openFeedId) classes.push("dispatch-row-open");
      return classes.join(" ");
    },
    [highlightedId, openFeedId, ticked.ticks],
  );

  return (
    <Box
      ref={pageRef}
      tabIndex={-1}
      sx={{
        bgcolor: zinc[900],
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        color: "white",
        outline: "none",
      }}
    >
      {/* One header row: what this is on the left, how it is narrowed and shaped on the right. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 1.5, pb: 1, flexWrap: "wrap" }}>
        <Typography component="h1" sx={{ fontSize: "1.125rem", fontWeight: 600, mr: 0.5 }}>
          Dispatch
        </Typography>
        <DispatchViewPicker view={definition} views={views} onOpen={onViewPickerOpen} onSelectView={switchView} />
        <DispatchTeamPicker team={team} teams={availableTeams} onChange={onTeamChange} />
        {/* At driver grain the count stays beside the selection; at message grain the selection takes its place. */}
        {(!tickedMode || resolved.grain === "driver") && (
          <DispatchCount
            visible={filtered.length}
            total={total}
            noun={resolved.grain}
            filterCount={filterCount}
            searching={searching}
          />
        )}
        {tickedMode && <DispatchSelectionBar count={tickedRows.length} onClear={ticked.clear} />}
        <DispatchBulkActions
          tickedCount={tickedRows.length}
          recipientCount={fleetRecipients.length}
          onAcknowledge={acknowledgeTickedByButton}
          onFleetMessage={openFleetMessage}
        />
        <DispatchSearchBox
          fields={resolved.query.search}
          tagsBySlug={tagsBySlug}
          noun={resolved.grain}
          search={query.search}
          onSearchChange={query.setSearch}
          inputRef={searchInputRef}
        />
        <Box sx={{ flexGrow: 1 }} />
        <DispatchFilterMenu
          controls={resolved.query.controls}
          tagsBySlug={tagsBySlug}
          controlValues={query.controlValues}
          onControlChange={query.setControl}
          optionsFor={query.valueOptionsFor}
          fieldFilters={options.fieldFilters}
          fieldOptionsFor={query.valueOptionsForField}
          chosenValuesFor={query.chosenValuesFor}
          onFieldValuesChange={query.setFieldValues}
          requiresAcknowledgement={requiresAcknowledgementControl}
          activeCount={filterCount}
          hidden={searching}
        />
        <DispatchDisplayMenu
          table={table}
          labelFor={layout.labelFor}
          viewColumnIds={layout.viewColumnIds}
          onColumnPinningChange={layout.onColumnPinningChange}
          groupBy={resolved.query.groupBy}
          groupOptions={options.groupByOptions}
          onGroupByChange={query.setGroupBy}
          sort={displaySort}
          sortOptions={options.sortOptions}
          onSortChange={query.chooseSort}
          onReset={reset}
          personalized={resolved.personalized}
          grain={resolved.grain}
          viewGrain={viewGrain}
          onGrainChange={changeGrain}
        />
        <DispatchShortcutsButton shown={shortcutBarShown} onToggle={toggleShortcutBar} buttonRef={setShortcutsButton} />
      </Box>
      <DispatchQueryStrip
        chips={chips}
        onClearAll={query.clearAll}
        filterCount={filterCount}
        hiddenCount={total - filtered.length}
        noun={resolved.grain}
        searching={searching}
        onClearSearch={query.clearSearch}
      />
      {shortcutBarShown && <DispatchShortcutsBar keymap={keymap} onShowAll={openShortcutsList} />}
      <Box
        ref={tableAreaRef}
        data-sb={TestIds.dispatch.tableArea}
        sx={{
          flex: 1,
          minHeight: 0,
          bgcolor: zinc[800],
          // A brand rule along the table's top edge while a search or filters narrow the rows, visible however the
          // header wraps. A border, since the sticky header's opaque cells would paint over an inset shadow.
          borderTop: searching || filterCount > 0 ? `2px solid ${brandPrimary[500]}` : undefined,
        }}
        onMouseOver={onTableMouseOver}
        onMouseLeave={clearHoveredRow}
      >
        <DispatchSearchHighlight.Provider value={searchHighlight}>
          <DispatchTable
            table={table}
            onRowClick={onRowClick}
            onRowContextMenu={openRowMenu}
            onHeaderClick={onHeaderClick}
            getRowClassName={getRowClassName}
            scrollToRowId={highlightedId}
            expandedRowId={tickedMode ? null : highlightedId}
            renderExpandedRow={renderExpandedRow}
            columnPinning={layout.columnPinning}
            onColumnPinningChange={layout.onColumnPinningChange}
            groups={query.grouped?.groups}
            onToggleGroup={query.toggleGroup}
            filteredColumnIds={query.filteredColumnIds}
            noRowsContent={
              <DispatchNoRows
                noun={resolved.grain}
                search={searchHighlight.term}
                filterCount={filterCount}
                total={total}
                onClearFilters={query.clearAll}
              />
            }
          />
        </DispatchSearchHighlight.Provider>
      </Box>
      <DispatchColumnMenu
        target={columnMenu}
        rows={query.projected}
        adhoc={resolved.query.adhoc}
        sort={displaySort}
        pinned={columnMenu != null && layout.columnPinning.left.includes(columnMenu.columnId)}
        canHide={(columnMenu && table.getColumn(columnMenu.columnId)?.getCanHide()) ?? false}
        onClose={() => setColumnMenu(null)}
        onSort={query.sortColumn}
        onReplaceConditions={query.replaceConditions}
        onTogglePin={layout.togglePin}
        onHide={layout.hide}
      />
      <DispatchRowMenu
        row={rowMenu?.row ?? null}
        fields={options.rowMenuFields}
        columnKey={rowMenu?.columnId ?? null}
        anchorPosition={rowMenu?.position ?? null}
        onClose={() => setRowMenu(null)}
        onAddCondition={query.addCondition}
      />
      <DispatchPopover
        open={openFeedId != null}
        panel={panel}
        feedId={openFeedId}
        driver={openDriver}
        tags={tags}
        phoneTagSlug={page.phoneTagSlug}
        workspaceId={workspaceId}
        now={now}
        onClose={route.closePanel}
        onPanelChange={changePanel}
        onSent={afterPanelReply}
        panelRef={panelRef}
      />
      <DispatchShortcutsList
        open={shortcutsListOpen}
        anchorEl={shortcutsButton}
        keymap={keymap}
        onClose={closeShortcutsList}
      />
      <DispatchFleetMessage
        open={fleetOpen}
        recipients={fleetRecipients}
        scope={tickedMode ? "ticked" : "shown"}
        filters={fleetFilters}
        onClose={() => setFleetOpen(false)}
        onSent={ticked.clear}
      />
    </Box>
  );
}
